import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RegistrationForm.css';

const RegistrationForm = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    manager: { fullName: '', email: '', phone: '', discord: '' },
    igl: { fullName: '', email: '', phone: '', discord: '', isPlayer: false },
    players: Array(5).fill({ ign: '', uid: '', serverId: '' }),
    substitutes: Array(2).fill({ ign: '', uid: '', serverId: '' }),
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 5;

  const handleInputChange = (section, index, field, value) => {
    setFormData(prev => {
      if (section === 'manager' || section === 'igl') {
        return {
          ...prev,
          [section]: { ...prev[section], [field]: value }
        };
      } else {
        const updated = [...prev[section]];
        updated[index] = { ...updated[index], [field]: value };
        return { ...prev, [section]: updated };
      }
    });
  };

  const validateMLBBPlayer = async (uid, serverId) => {
    try {
      const response = await fetch('http://localhost:5000/api/validate-player', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, serverId })
      });
      const data = await response.json();
      return data.valid;
    } catch (error) {
      console.error('Validation error:', error);
      return false;
    }
  };

  const validateStep = async () => {
    const errors = {};

    if (currentStep === 1) {
      // Manager validation
      if (!formData.manager.fullName) errors.managerName = 'Full name required';
      if (!formData.manager.email || !/\S+@\S+\.\S+/.test(formData.manager.email)) {
        errors.managerEmail = 'Valid email required';
      }
      if (!formData.manager.phone) errors.managerPhone = 'Phone number required';
      if (!formData.manager.discord) errors.managerDiscord = 'Discord ID required';
    }

    if (currentStep === 2) {
      // IGL validation
      if (!formData.igl.fullName) errors.iglName = 'Full name required';
      if (!formData.igl.email || !/\S+@\S+\.\S+/.test(formData.igl.email)) {
        errors.iglEmail = 'Valid email required';
      }
      if (!formData.igl.discord) errors.iglDiscord = 'Discord ID required';
    }

    if (currentStep === 3) {
      // Players validation
      for (let i = 0; i < 5; i++) {
        const player = formData.players[i];
        if (!player.ign) errors[`player${i}ign`] = 'IGN required';
        if (!player.uid) errors[`player${i}uid`] = 'UID required';
        if (!player.serverId) errors[`player${i}server`] = 'Server ID required';
        
        // MLBB validation
        if (player.uid && player.serverId) {
          const isValid = await validateMLBBPlayer(player.uid, player.serverId);
          if (!isValid) errors[`player${i}uid`] = 'Invalid UID or Server ID';
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    const isValid = await validateStep();
    if (isValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch('http://localhost:5000/api/register-team', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        alert('Team registered successfully!');
        onClose();
      } else {
        alert('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="form-section">
            <h3>Manager Details</h3>
            <p className="form-note">Manager role is optional but recommended</p>
            
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={formData.manager.fullName}
                onChange={(e) => handleInputChange('manager', null, 'fullName', e.target.value)}
                className={validationErrors.managerName ? 'error' : ''}
              />
              {validationErrors.managerName && <span className="error-text">{validationErrors.managerName}</span>}
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.manager.email}
                onChange={(e) => handleInputChange('manager', null, 'email', e.target.value)}
                className={validationErrors.managerEmail ? 'error' : ''}
              />
              {validationErrors.managerEmail && <span className="error-text">{validationErrors.managerEmail}</span>}
            </div>

            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                value={formData.manager.phone}
                onChange={(e) => handleInputChange('manager', null, 'phone', e.target.value)}
                className={validationErrors.managerPhone ? 'error' : ''}
              />
              {validationErrors.managerPhone && <span className="error-text">{validationErrors.managerPhone}</span>}
            </div>

            <div className="form-group">
              <label>Discord ID *</label>
              <input
                type="text"
                value={formData.manager.discord}
                onChange={(e) => handleInputChange('manager', null, 'discord', e.target.value)}
                placeholder="username#1234"
                className={validationErrors.managerDiscord ? 'error' : ''}
              />
              {validationErrors.managerDiscord && <span className="error-text">{validationErrors.managerDiscord}</span>}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="form-section">
            <h3>In-Game Leader (IGL)</h3>
            <p className="form-note">IGL can be one of the main players</p>
            
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.igl.isPlayer}
                  onChange={(e) => handleInputChange('igl', null, 'isPlayer', e.target.checked)}
                />
                IGL is also a player
              </label>
            </div>

            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={formData.igl.fullName}
                onChange={(e) => handleInputChange('igl', null, 'fullName', e.target.value)}
                className={validationErrors.iglName ? 'error' : ''}
              />
              {validationErrors.iglName && <span className="error-text">{validationErrors.iglName}</span>}
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.igl.email}
                onChange={(e) => handleInputChange('igl', null, 'email', e.target.value)}
                className={validationErrors.iglEmail ? 'error' : ''}
              />
              {validationErrors.iglEmail && <span className="error-text">{validationErrors.iglEmail}</span>}
            </div>

            <div className="form-group">
              <label>Discord ID *</label>
              <input
                type="text"
                value={formData.igl.discord}
                onChange={(e) => handleInputChange('igl', null, 'discord', e.target.value)}
                placeholder="username#1234"
                className={validationErrors.iglDiscord ? 'error' : ''}
              />
              {validationErrors.iglDiscord && <span className="error-text">{validationErrors.iglDiscord}</span>}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="form-section">
            <h3>Main Players (5 Required)</h3>
            <p className="form-note">All players will be verified through MLBB API</p>
            
            {formData.players.map((player, index) => (
              <div key={index} className="player-entry">
                <h4>Player {index + 1}</h4>
                
                <div className="form-group">
                  <label>In-Game Name (IGN) *</label>
                  <input
                    type="text"
                    value={player.ign}
                    onChange={(e) => handleInputChange('players', index, 'ign', e.target.value)}
                    className={validationErrors[`player${index}ign`] ? 'error' : ''}
                  />
                  {validationErrors[`player${index}ign`] && <span className="error-text">{validationErrors[`player${index}ign`]}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>UID *</label>
                    <input
                      type="text"
                      value={player.uid}
                      onChange={(e) => handleInputChange('players', index, 'uid', e.target.value)}
                      className={validationErrors[`player${index}uid`] ? 'error' : ''}
                    />
                    {validationErrors[`player${index}uid`] && <span className="error-text">{validationErrors[`player${index}uid`]}</span>}
                  </div>

                  <div className="form-group">
                    <label>Server ID *</label>
                    <input
                      type="text"
                      value={player.serverId}
                      onChange={(e) => handleInputChange('players', index, 'serverId', e.target.value)}
                      className={validationErrors[`player${index}server`] ? 'error' : ''}
                    />
                    {validationErrors[`player${index}server`] && <span className="error-text">{validationErrors[`player${index}server`]}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 4:
        return (
          <div className="form-section">
            <h3>Substitute Players (Up to 2)</h3>
            <p className="form-note">Substitutes are optional but recommended</p>
            
            {formData.substitutes.map((sub, index) => (
              <div key={index} className="player-entry">
                <h4>Substitute {index + 1} (Optional)</h4>
                
                <div className="form-group">
                  <label>In-Game Name (IGN)</label>
                  <input
                    type="text"
                    value={sub.ign}
                    onChange={(e) => handleInputChange('substitutes', index, 'ign', e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>UID</label>
                    <input
                      type="text"
                      value={sub.uid}
                      onChange={(e) => handleInputChange('substitutes', index, 'uid', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Server ID</label>
                    <input
                      type="text"
                      value={sub.serverId}
                      onChange={(e) => handleInputChange('substitutes', index, 'serverId', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 5:
        return (
          <div className="form-section confirmation-section">
            <h3>Review & Confirm</h3>
            <p className="form-note">Please review all information before submitting</p>
            
            <div className="confirmation-block">
              <h4>Manager</h4>
              <p>{formData.manager.fullName} - {formData.manager.email}</p>
            </div>

            <div className="confirmation-block">
              <h4>In-Game Leader</h4>
              <p>{formData.igl.fullName} - {formData.igl.email}</p>
            </div>

            <div className="confirmation-block">
              <h4>Main Players</h4>
              {formData.players.map((player, index) => (
                player.ign && <p key={index}>{index + 1}. {player.ign} (UID: {player.uid})</p>
              ))}
            </div>

            <div className="confirmation-block">
              <h4>Substitutes</h4>
              {formData.substitutes.map((sub, index) => (
                sub.ign && <p key={index}>{index + 1}. {sub.ign} (UID: {sub.uid})</p>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="registration-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="modal-close" onClick={onClose}>✕</button>
          
          <div className="modal-header">
            <h2>Team Registration</h2>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
            <p className="step-indicator">Step {currentStep} of {totalSteps}</p>
          </div>

          <div className="modal-body">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="modal-footer">
            {currentStep > 1 && (
              <button 
                className="btn btn-secondary"
                onClick={handlePrevious}
              >
                Previous
              </button>
            )}
            
            {currentStep < totalSteps ? (
              <button 
                className="btn btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button 
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RegistrationForm;
